import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StarRating, ComplianceNote } from "@/components/kit";
import { Calendar, Share2, Check, Gift } from "lucide-react";
import { toast } from "sonner";

export default function RegistryDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { window.scrollTo(0, 0); api.get(`/registries/${id}`).then((x) => setR(x.data)); }, [id]);
  if (!r) return <div className="p-12 text-center text-muted-foreground">Loading registry…</div>;

  const items = r.items.filter((i) => filter === "all" ? true : filter === "remaining" ? i.received < i.qty : i.received >= i.qty);

  return (
    <div>
      <div className="relative h-56 overflow-hidden lg:h-72">
        <img src={r.image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="absolute bottom-0 mx-auto w-full max-w-5xl px-6 pb-6">
          <span className="overline text-primary">{r.occasion} · {r.theme}</span>
          <h1 className="mt-1 font-display text-4xl lg:text-5xl">{r.title}</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><span>Hosted by {r.host}</span> · <Calendar size={13} /> {r.date}</p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-8">
        {/* progress */}
        <div className="mb-8 flex flex-col gap-4 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <div className="flex justify-between text-sm"><span className="font-semibold">{r.received} of {r.total} gifts received</span><span className="text-primary">{r.progress}%</span></div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${r.progress}%` }} /></div>
          </div>
          <button data-testid="share-registry-btn" onClick={() => toast.success("Registry link copied")} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary"><Share2 size={15} /> Share link</button>
        </div>

        <div className="mb-5 flex gap-2">
          {[["all", "All items"], ["remaining", "Still needed"], ["received", "Received"]].map(([k, l]) => (
            <button key={k} data-testid={`registry-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full border px-4 py-1.5 text-sm font-medium ${filter === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>{l}</button>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((it) => {
            const done = it.received >= it.qty;
            return (
              <div key={it.id} data-testid={`registry-item-${it.id}`} className="flex gap-4 rounded-xl border border-border bg-card p-4">
                <img src={it.image} alt="" className="h-24 w-24 shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <Link to={`/ratings/${it.id}`} className="font-semibold hover-underline">{it.name}</Link>
                  <div className="flex items-center gap-2"><StarRating value={it.rating} size={11} /><span className="text-xs text-muted-foreground">${it.price}</span></div>
                  <p className="mt-1 text-xs text-muted-foreground">{it.received} of {it.qty} gifted</p>
                  {done ? (
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-secondary/15 px-3 py-1 text-xs font-semibold text-secondary dark:text-secondary-foreground"><Check size={13} /> Fully gifted</span>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <button data-testid={`send-gift-${it.id}`} onClick={() => toast.success("Sending gift", { description: "Routed to partner checkout." })} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"><Gift size={12} /> Send as Gift</button>
                      <button onClick={() => toast.success("Item reserved")} className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary">Reserve</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-8"><ComplianceNote /></div>
      </div>
    </div>
  );
}
